export const blogPosts = [
  {
    slug: 'what-is-a-public-access-file',
    title: 'What Is a Public Access File? The Complete H-1B PAF Guide for Employers',
    excerpt: 'Everything employers need to know about maintaining Public Access Files for H-1B, H-1B1, and E-3 visa workers — what to include, when to create one, and how to stay DOL compliant.',
    category: 'PAF Basics',
    readTime: '10 min read',
    date: 'March 28, 2026',
    featured: true,
    content: `
## What Is a Public Access File?

A Public Access File (PAF) is a collection of documents that every U.S. employer must create and maintain for each H-1B, H-1B1, or E-3 visa worker they employ. This requirement comes directly from the Department of Labor (DOL) regulations under 20 CFR 655.760.

The PAF must be available for public inspection within **one business day** after the Labor Condition Application (LCA) is filed with the DOL. This means that any member of the public — including current employees, union representatives, or DOL investigators — can request to see the file.

## Why Does the Public Access File Exist?

The PAF exists to ensure transparency in the H-1B program. Congress designed this requirement so that:

- **American workers** can verify that H-1B employees are being paid the required prevailing wage
- **The DOL** can quickly review compliance during audits or investigations
- **Employees** can confirm their own working conditions match what was filed

The underlying principle is simple: if you're sponsoring a foreign worker, you should be able to prove you're doing it by the rules.

## What Must Be Included in a Public Access File?

The DOL requires the following documents in every PAF:

### 1. The LCA (Form ETA-9035/9035E)
A copy of the certified Labor Condition Application, including all pages. This is the foundation document of every PAF.

### 2. Prevailing Wage Documentation
Evidence of the prevailing wage for the occupation in the area of intended employment. This can be:
- A prevailing wage determination (PWD) from the DOL's National Prevailing Wage Center
- A published wage survey that meets DOL requirements
- Other legitimate wage data sources as defined by DOL regulations

### 3. Actual Wage Documentation
A memo or document explaining how the employer determined the actual wage paid to the H-1B worker relative to other employees in similar positions. This is often called the "wage memo" or "actual wage memo."

### 4. Explanation of the Wage System
Documentation explaining how the employer's pay system works — salary ranges, pay grades, or other compensation structures that apply to the position.

### 5. Benefits Documentation
Information showing that the H-1B worker receives benefits on the same basis as similarly employed U.S. workers.

### 6. Notice of LCA Posting
Evidence that the employer provided notice of the LCA filing. This can be:
- A copy of the notice posted at the worksite for 10 business days
- Evidence of electronic posting (e.g., intranet notification)
- If unionized, evidence that the bargaining representative was notified

### 7. Summary of Benefits (for H-1B Dependent Employers)
If the employer is H-1B dependent, additional documentation may be required regarding recruitment efforts and displacement of U.S. workers.

## When Must the PAF Be Created?

The PAF must be created and available for inspection **no later than one business day after the LCA is filed** with the DOL. This is a critical deadline that many employers miss.

Key timing requirements:
- **Day of LCA filing:** Start assembling the PAF
- **One business day after filing:** PAF must be complete and available
- **Throughout employment:** Keep the PAF updated with any changes
- **After employment ends:** Retain the PAF for one year after the LCA expires or one year after the H-1B worker's employment ends, whichever is later

## Where Must the PAF Be Kept?

The PAF must be maintained at either:
- The employer's principal place of business, or
- The worksite where the H-1B worker is employed

It must be available for inspection by any member of the public during normal business hours.

## What Happens If You Don't Maintain a PAF?

Failure to maintain a proper Public Access File can result in:

- **Civil monetary penalties** of up to $1,000 per violation per worker
- **Back pay** awards to affected workers
- **Debarment** from the H-1B program for up to three years for willful violations
- **Increased scrutiny** in future LCA filings and visa petitions

The DOL's Wage and Hour Division (WHD) is the agency responsible for enforcing these requirements, and they can initiate investigations based on complaints or through random audits.

## Common PAF Mistakes Employers Make

1. **Creating the PAF too late** — waiting until after the visa is approved instead of within one business day of LCA filing
2. **Missing the wage memo** — forgetting to document the actual wage determination
3. **No posting evidence** — failing to keep proof that the LCA notice was posted
4. **Not updating the file** — leaving the PAF unchanged when wages, worksites, or conditions change
5. **Mixing up employees** — putting documents for the wrong employee in the wrong file
6. **Losing track of files** — not having a system to quickly locate and produce a PAF when requested

## How PAFHub Helps

PAFHub was built specifically to solve these problems. Instead of managing PAFs with spreadsheets and filing cabinets, PAFHub provides:

- **Document checklists** that show exactly what's missing from each PAF
- **Deadline tracking** so you know when LCAs are expiring
- **One-click packet generation** that merges all documents into an audit-ready PDF
- **Public access links** that make it easy to share PAFs when requested
- **Audit trail** logging every document upload, edit, and access

The result? You spend less time worrying about compliance and more time on what matters — supporting your team.
    `,
  },
  {
    slug: 'h1b-vs-h1b1-vs-e3-paf-requirements',
    title: 'H-1B vs. H-1B1 vs. E-3: How PAF Requirements Differ by Visa Type',
    excerpt: 'While H-1B, H-1B1, and E-3 visas all require Public Access Files, the specific requirements vary. Here\'s what employers need to know about each visa category.',
    category: 'Compliance',
    readTime: '7 min read',
    date: 'March 20, 2026',
    featured: false,
    content: `
## Understanding the Three Visa Categories

All three visa categories — H-1B, H-1B1, and E-3 — require employers to file Labor Condition Applications (LCAs) and maintain Public Access Files. However, there are important differences employers should understand.

## H-1B: The Standard Specialty Occupation Visa

The H-1B is the most common employment-based visa for specialty occupation workers. It is available to nationals of any country.

**PAF Requirements:**
- Full LCA (Form ETA-9035/9035E)
- Prevailing wage documentation
- Actual wage memo
- Wage system explanation
- Benefits documentation
- LCA posting notice (10 business days)
- Additional H-1B dependent employer documentation (if applicable)

**Key Considerations:**
- Subject to annual cap (85,000 per fiscal year, including 20,000 for advanced degree holders)
- Maximum initial stay of 3 years, extendable to 6 years
- H-1B dependent employers have additional attestation requirements

## H-1B1: The Free Trade Agreement Visa

The H-1B1 is available only to nationals of **Chile** and **Singapore** under free trade agreements with the United States.

**PAF Requirements:**
Similar to H-1B, with these distinctions:
- LCA is filed using the same Form ETA-9035/9035E
- Same document requirements as H-1B PAFs
- Posting requirements are identical

**Key Differences from H-1B:**
- **No petition required** — the worker applies directly at a U.S. consulate
- Has its own annual allocation (1,400 for Chile, 5,400 for Singapore)
- Unused numbers roll into the H-1B cap
- Maximum stay of 1 year at a time, but can be renewed indefinitely
- The employer still files an LCA and must maintain a PAF

**Common Mistake:** Many employers assume H-1B1 workers don't need PAFs because no petition is filed with USCIS. This is incorrect — the LCA requirement and PAF obligation still apply.

## E-3: The Australian Specialty Occupation Visa

The E-3 is exclusively for nationals of **Australia** and their dependents.

**PAF Requirements:**
- LCA filing is required (same Form ETA-9035/9035E)
- Same core PAF documents as H-1B
- Posting requirements are identical

**Key Differences from H-1B:**
- **Only for Australians** — a dedicated visa category
- Annual cap of 10,500
- Maximum initial stay of 2 years, renewable indefinitely
- Spouses can apply for work authorization (unlike most H-1B spouses)
- The employer must file an LCA, and the PAF obligation is the same

## Side-by-Side Comparison

| Requirement | H-1B | H-1B1 | E-3 |
|---|---|---|---|
| LCA Required | Yes | Yes | Yes |
| PAF Required | Yes | Yes | Yes |
| Posting Notice | 10 days | 10 days | 10 days |
| Prevailing Wage Doc | Required | Required | Required |
| Actual Wage Memo | Required | Required | Required |
| Benefits Documentation | Required | Required | Required |
| PAF Timing | 1 business day after LCA filing | 1 business day after LCA filing | 1 business day after LCA filing |
| Retention Period | 1 year after LCA expiry | 1 year after LCA expiry | 1 year after LCA expiry |

## The Bottom Line

From a PAF compliance perspective, the requirements are essentially the same across all three visa types. The LCA filing triggers the PAF obligation, and the same documents must be maintained regardless of whether the worker holds an H-1B, H-1B1, or E-3 visa.

PAFHub supports all three visa types and tracks the specific visa category for each employee, so you can manage compliance across your entire international workforce in one place.
    `,
  },
  {
    slug: 'dol-audit-preparation-checklist',
    title: 'DOL Audit Preparation: A Step-by-Step Checklist for H-1B Employers',
    excerpt: 'A DOL audit doesn\'t have to be stressful. Follow this step-by-step checklist to ensure your Public Access Files and LCA compliance are audit-ready at all times.',
    category: 'Audit Readiness',
    readTime: '8 min read',
    date: 'March 12, 2026',
    featured: false,
    content: `
## Why DOL Audits Happen

The Department of Labor's Wage and Hour Division (WHD) enforces LCA compliance through two primary mechanisms:

1. **Complaint-driven investigations** — initiated when a worker, former worker, or third party files a complaint
2. **Random audits** — the DOL periodically selects employers for compliance review

In either case, the DOL will request your Public Access Files, often with little advance notice. Being prepared means having your files organized, complete, and accessible at all times.

## Before the Audit: Proactive Preparation

### Step 1: Inventory Your H-1B, H-1B1, and E-3 Workers
Create a complete list of all current and recent employees on LCA-dependent visas. For each worker, note:
- Full name and visa type
- LCA case number and filing date
- LCA expiration date
- Current worksite location
- Current wage being paid

### Step 2: Review Each PAF for Completeness
For every employee, verify that the PAF contains:
- [ ] Certified LCA (all pages)
- [ ] Prevailing wage documentation
- [ ] Actual wage memo/determination
- [ ] Wage system explanation
- [ ] Benefits documentation
- [ ] LCA posting evidence (10-day notice)
- [ ] Any amendments or updated LCAs

### Step 3: Verify Wage Compliance
For each H-1B worker, confirm:
- The actual wage paid meets or exceeds the prevailing wage on the LCA
- The worker is being paid for all hours worked (no "benching")
- Any changes in duties, location, or wages have been reflected in a new or amended LCA

### Step 4: Check Posting Compliance
Verify that:
- Posting notices were displayed for the required 10 business days
- You have retained proof of the posting (printed or electronic)
- Postings were done at the actual worksite, not just headquarters

### Step 5: Confirm File Accessibility
Ensure:
- PAFs are stored in an accessible location (principal office or worksite)
- Files can be produced within one business day of a request
- Someone in your organization knows where the files are and how to retrieve them

## During the Audit

### What to Expect
- The DOL investigator will request specific PAFs by employee name or LCA number
- They may interview the H-1B worker and/or HR personnel
- They will review payroll records to verify wage payments
- The investigation may cover multiple years of employment

### How to Respond
- **Be cooperative** — refusing to provide documents escalates the situation
- **Provide exactly what's requested** — don't volunteer additional information
- **Document everything** — keep records of what was requested and provided
- **Consult counsel** — if you have concerns, involve your immigration attorney early

## After the Audit

### If No Violations Are Found
- Maintain your current practices
- Use the experience to improve your compliance processes

### If Violations Are Found
The DOL may impose:
- Back pay obligations
- Civil monetary penalties (up to $1,000 per violation per worker)
- In severe cases, debarment from the H-1B program

You typically have the right to respond and negotiate before penalties are finalized.

## How PAFHub Keeps You Audit-Ready

Instead of scrambling when the DOL calls, PAFHub ensures you're always prepared:

- **Real-time compliance dashboard** shows the status of every PAF at a glance
- **Document checklists** flag missing items before they become audit findings
- **One-click PDF generation** creates complete, formatted PAF packets instantly
- **Expiration alerts** warn you 30 and 7 days before LCA deadlines
- **Audit trail** provides a timestamped record of every action taken on every file

The goal is simple: when the DOL calls, you should be able to produce a complete PAF within minutes, not hours or days.
    `,
  },
  {
    slug: 'lca-posting-requirements-guide',
    title: 'LCA Posting Requirements: What Employers Get Wrong (and How to Fix It)',
    excerpt: 'The LCA posting notice is one of the most commonly misunderstood PAF requirements. Learn exactly what the DOL requires and how to document it properly.',
    category: 'Compliance',
    readTime: '6 min read',
    date: 'March 5, 2026',
    featured: false,
    content: `
## The Posting Requirement Explained

When an employer files a Labor Condition Application (LCA) for an H-1B, H-1B1, or E-3 worker, they must provide notice to their existing workforce. This is done through posting the LCA or a summary at the worksite.

The posting requirement serves a critical transparency purpose: it gives current employees the opportunity to know that a foreign worker is being hired and at what wage, so they can raise concerns if they believe it affects their own working conditions.

## What Must Be Posted

The notice must include:
- The fact that an LCA has been or will be filed
- The number of H-1B workers being sought
- The occupational classification (SOC code and title)
- The wage rate offered
- The period of employment
- The worksite locations

You can post either a copy of the actual LCA or a separate notice containing this information.

## Where to Post

**Non-union workplaces:**
The notice must be posted in **two conspicuous locations** at each worksite where the H-1B worker will be employed. "Conspicuous" means places where employees would normally see posted notices — break rooms, bulletin boards, near time clocks, etc.

Alternatively, the notice can be posted electronically — on the company intranet, via email to all employees, or through other electronic means reasonably calculated to provide notice.

**Union workplaces:**
Instead of workplace posting, the employer must provide notice directly to the bargaining representative (union). The notice must contain the same information.

## How Long to Post

The notice must remain posted for **10 consecutive business days**. This is calendar business days — weekends and company holidays don't count.

**Timing:**
- Posting must begin no more than 30 days before the LCA filing date
- And no later than the date the LCA is filed

## Common Posting Mistakes

### 1. Only Posting at Headquarters
If the H-1B worker will be at a client site or a branch office, posting only at HQ doesn't satisfy the requirement. You must post at the actual worksite.

### 2. Posting for Less Than 10 Days
Some employers take down the notice after a week. The full 10 business days must be completed.

### 3. Not Retaining Proof
You must keep evidence that the posting was done. This means:
- A copy of what was posted
- Documentation of where and when it was posted
- Evidence of the 10-day posting period (start and end dates)

### 4. Electronic Posting Without Adequate Access
If posting electronically, all employees at the worksite must have access to the electronic medium used. A posting on a restricted intranet that contract workers can't access doesn't count.

### 5. Not Posting for Amended LCAs
When you file an amended LCA (e.g., for a worksite change or wage change), you must do a new posting for the amended LCA. The original posting doesn't carry over.

## Best Practices for Posting Compliance

1. **Create a standard posting template** that includes all required information
2. **Take dated photos** of physical postings as evidence
3. **Save screenshots** of electronic postings with timestamps
4. **Track posting periods** with start and end dates for each LCA
5. **Include posting evidence** in the PAF immediately — don't wait

## How PAFHub Helps

PAFHub's document checklist specifically tracks LCA posting evidence for each PAF. When the posting document is missing, the compliance status reflects it — so you always know which PAFs need attention before an auditor does.
    `,
  },
  {
    slug: 'prevailing-wage-actual-wage-explained',
    title: 'Prevailing Wage vs. Actual Wage: What Every H-1B Employer Must Document',
    excerpt: 'Understanding the difference between prevailing wage and actual wage — and documenting both correctly — is essential for PAF compliance. Here\'s what you need to know.',
    category: 'PAF Basics',
    readTime: '7 min read',
    date: 'February 25, 2026',
    featured: false,
    content: `
## The Two-Wage Requirement

Under DOL regulations, H-1B employers must pay the higher of two wages:

1. **The Prevailing Wage** — the average wage paid to similarly employed workers in the geographic area
2. **The Actual Wage** — the wage the employer pays to other employees with similar experience and qualifications in the same position

This "higher of the two" rule is a core protection of the H-1B program, designed to ensure that hiring foreign workers doesn't depress wages for American workers.

## Prevailing Wage: What It Is and How to Document It

The prevailing wage is the average wage paid to workers in the same occupation in the same geographic area. Employers can determine it through several methods:

### Option 1: DOL Prevailing Wage Determination (PWD)
The most common and safest approach. You submit a request to the DOL's National Prevailing Wage Center (NPWC), and they provide an official wage determination based on:
- The occupation (SOC code)
- The geographic area (MSA)
- The skill level (Levels I through IV)

**For your PAF:** Include the official PWD letter from the NPWC.

### Option 2: Independent Survey
Employers can use a published wage survey that meets specific DOL criteria:
- Reflects the geographic area and occupation
- Is based on data collected within 24 months
- Represents a reasonable cross-section of employers
- Uses recognized statistical methods

**For your PAF:** Include the relevant pages of the survey showing the methodology, sample size, and wage data.

### Option 3: Other Legitimate Sources
This includes data from state workforce agencies or educational institution surveys for certain positions.

**For your PAF:** Include the source documentation with an explanation of how it meets DOL standards.

## Actual Wage: What It Is and How to Document It

The actual wage is the wage paid by the employer to all other individuals with similar experience and qualifications for the specific employment in question.

### How to Determine the Actual Wage
1. Identify all employees in the same or substantially similar position
2. Consider their experience, education, and skill levels
3. Determine the wage range for that group
4. Ensure the H-1B worker's wage falls within or above this range

### The Actual Wage Memo
The actual wage memo (also called the "wage analysis memo") is one of the most important PAF documents. It should include:

- **The position being analyzed** — job title, duties, and department
- **Comparable employees** — a description of the pool of similarly employed workers (you don't need to list names, but you need to describe the comparison group)
- **Factors considered** — experience, education, specialization, and other relevant qualifications
- **The wage determination** — the actual wage paid to comparable employees and how the H-1B worker's wage compares
- **Conclusion** — confirmation that the H-1B wage meets or exceeds the actual wage

### Important: You Don't Need to Disclose Individual Salaries
The actual wage memo should describe the wage system and comparison methodology. You are NOT required to disclose specific employees' names or individual salary figures in the PAF.

## Putting It Together: The PAF Wage Documentation

Your PAF should contain three wage-related documents:

1. **Prevailing wage documentation** — the PWD letter, survey, or other source
2. **Actual wage memo** — your analysis of what comparable employees are paid
3. **Wage system explanation** — a description of your pay structure (salary ranges, pay grades, how wages are set)

Together, these documents demonstrate that you've done your homework and that the H-1B worker's compensation complies with DOL requirements.

## Red Flags the DOL Looks For

- H-1B workers paid at exactly the prevailing wage minimum with no raises over time
- No actual wage analysis on file
- Prevailing wage documentation that doesn't match the job duties or worksite
- Discrepancies between the LCA wage and actual payroll records
- Missing or incomplete wage system explanation

## How PAFHub Helps

PAFHub's document checklist tracks all three wage documentation requirements for each PAF. The compliance status clearly shows when wage documents are missing, so you can address gaps before they become audit findings.
    `,
  },
];
